import React from 'react';
import axios from 'axios';
import Select from '@ordermentum/ui-shared/atoms/SelectV2';
import Button from '@ordermentum/ui-shared/atoms/Button';
import * as yup from 'yup';
import { FormikBag, withFormik, FormikProps } from 'formik';
import LabelledInput from '@ordermentum/ui-shared/molecules/LabelledInput';
import Label from '@ordermentum/ui-shared/atoms/Label';
import FieldError from '@ordermentum/ui-shared/molecules/FieldError';
import Spinner from '@ordermentum/ui-shared/atoms/Spinner';
import { Location } from '@ordermentum/ui-definitions';
import { trackEvent, TrackEvent } from '../../Analytics/redux/actions';
import {
  ACCOUNTING_SOFTWARE_OPTION,
  BUSINESS_TYPE_OPTIONS,
  REFERRAL_OPTIONS,
  STATES_OPTIONS,
} from '../constants';
import { TRACK_EVENT_FORM_SUBMISSION } from '../../Analytics/redux/constants';
import { changeWindowLocation } from '../../../lib/utilities';

const FieldWrapper = ({ children }: { children: React.ReactNode }) => (
  <div className="mb-l w-full">{children}</div>
);

type Values = {
  company: string;
  customerTotal: number;
  accountingSoftware: string;
  referral: string;
  businessType: string;
  state: string;
};

type OwnProps = {
  userId: string;
  location: Location;
  trackEvent: TrackEvent;
};

type Props = OwnProps & FormikProps<Values>;
const BusinessFormV2 = ({
  setFieldValue,
  handleSubmit,
  isSubmitting,
  values,
  errors,
  setFieldError,
}: Props) => {
  const onChange = (tag, value) => {
    setFieldValue(tag, value);
    setFieldError(tag, '');
  };

  return (
    <form onSubmit={handleSubmit}>
      <FieldWrapper>
        <LabelledInput
          label="Business Name"
          tag="company"
          value={values.company}
          hasError={!!errors.company}
          onChange={onChange}
          isInactive={isSubmitting}
          size="max"
        />
        {errors.company && <FieldError message={errors.company} />}
      </FieldWrapper>

      <FieldWrapper>
        <LabelledInput
          label="How many customers do you have?"
          tag="customerTotal"
          hasError={!!errors.customerTotal}
          value={values.customerTotal}
          onChange={onChange}
          isInactive={isSubmitting}
          size="max"
        />
        {errors.customerTotal && <FieldError message={errors.customerTotal} />}
      </FieldWrapper>

      <FieldWrapper>
        <Label
          htmlFor="accountingSoftware"
          label="Which accounting platform do you use?"
          required
        >
          <Select
            size="large"
            name="accountingSoftware"
            value={values.accountingSoftware}
            error={!!errors.accountingSoftware}
            options={ACCOUNTING_SOFTWARE_OPTION}
            onChange={onChange}
            disabled={isSubmitting}
          />
        </Label>
        {errors.accountingSoftware && (
          <FieldError message={errors.accountingSoftware} />
        )}
      </FieldWrapper>

      <FieldWrapper>
        <Label
          htmlFor="businessType"
          label="What industry are you in?"
          required
        >
          <Select
            size="large"
            name="businessType"
            value={values.businessType}
            error={!!errors.businessType}
            options={BUSINESS_TYPE_OPTIONS}
            onChange={onChange}
            disabled={isSubmitting}
          />
        </Label>
        {errors.businessType && <FieldError message={errors.businessType} />}
      </FieldWrapper>

      <FieldWrapper>
        <Label
          htmlFor="referral"
          label="How did you hear about Ordermentum?"
          required
        >
          <Select
            size="large"
            name="referral"
            value={values.referral}
            error={!!errors.referral}
            options={REFERRAL_OPTIONS}
            onChange={onChange}
            disabled={isSubmitting}
          />
        </Label>
        {errors.referral && <FieldError message={errors.referral} />}
      </FieldWrapper>

      <FieldWrapper>
        <Label htmlFor="state" label="Which state are you in?" required>
          <Select
            size="large"
            name="state"
            value={values.state}
            error={!!errors.state}
            options={STATES_OPTIONS}
            onChange={onChange}
            disabled={isSubmitting}
          />
        </Label>
        {errors.state && <FieldError message={errors.state} />}
      </FieldWrapper>

      <FieldWrapper>
        <Button size="max" type="primary-submit" isInactive={isSubmitting}>
          {isSubmitting ? (
            <Spinner color="white" size="sm" />
          ) : (
            'Start your free trial'
          )}
        </Button>
      </FieldWrapper>
    </form>
  );
};

const validationSchema = yup.object().shape({
  company: yup.string().trim().required('Please enter a business name'),
  customerTotal: yup
    .number()
    .min(0)
    .typeError('Customer total should be a valid number'),
  accountingSoftware: yup
    .string()
    .oneOf(ACCOUNTING_SOFTWARE_OPTION.map(x => x.tag))
    .required('Please select an option'),
  referral: yup.string().required('Please select an option'),
  businessType: yup
    .string()
    .required('Please select an option')
    .oneOf(BUSINESS_TYPE_OPTIONS.map(x => x.tag)),
  state: yup.string(),
});

export default withFormik<OwnProps, Values>({
  enableReinitialize: true,
  validateOnChange: false,
  isInitialValid: true,
  handleSubmit: async (values: Values, formikBag: FormikBag<Props, Values>) => {
    const { props } = formikBag;
    const { userId, location } = props;
    const {
      company,
      customerTotal,
      accountingSoftware,
      referral,
      businessType,
      state,
    } = values;
    try {
      const campaign = location.query?.campaign ?? 'app-sign-up';
      trackEvent({
        event: TRACK_EVENT_FORM_SUBMISSION,
        type: 'business-details-form-submission',
        properties: {
          campaign,
        },
      });

      await axios.post('/v1/register/contact', {
        company,
        customer_total: customerTotal,
        accounting_software: accountingSoftware,
        referral,
        business_type: businessType,
        userId,
        campaign,
        state,
      });
      changeWindowLocation('/register/success');
    } catch (err) {
      formikBag.setSubmitting(false);
    }
  },
  validationSchema,
})(BusinessFormV2);
